import * as z from 'zod';

// MICA/ADR MI company codes (see MICA Delinquency (ADR) Record Specification, code table 8).
// FHA and VA codes from the source table are omitted: those loans report through TS 264, not the ADR.
export const MiCompanyCode = z.enum([
  '001', // Enact (formerly Genworth)
  '003', // MGIC
  '004', // PMI Mortgage Insurance Company
  '011', // Republic Mortgage Insurance Company
  '013', // United Guaranty Residential Insurance Company
  '019', // Triad Guaranty Insurance Company
  '033', // Radian Guaranty Inc.
  '038', // Arch Mortgage Insurance Company
  '039', // United Guaranty Mortgage Indemnity Co.
  '043', // Essent Guaranty
  '044', // National Mortgage Insurance Corporation
]);
export type MiCompanyCode = z.infer<typeof MiCompanyCode>;

export const MI_COMPANY_CODE_LABELS: Record<MiCompanyCode, string> = {
  '001': 'Enact',
  '003': 'MGIC',
  '004': 'PMI Mortgage Insurance',
  '011': 'Republic MI',
  '013': 'United Guaranty Residential',
  '019': 'Triad Guaranty',
  '033': 'Radian',
  '038': 'Arch MI',
  '039': 'United Guaranty Mortgage Indemnity',
  '043': 'Essent',
  '044': 'National MI',
};

export const formatMiCompanyCode = (code: MiCompanyCode): string => `${code} - ${MI_COMPANY_CODE_LABELS[code]}`;

// Fallback resolution when a payee has no explicit miCompanyCode. Patterns are matched
// against a punctuation-stripped, lowercased payee name, most-specific first, and on whole-word
// boundaries so that e.g. "Monarch Insurance Services" does not match the `arch` pattern (Arch).
// Order matters: `united guaranty mortgage indemnity` (039) must be tested before the shorter
// `united guaranty` (013), and `national mortgage insurance` before `national mi`.
const MI_COMPANY_NAME_PATTERNS: ReadonlyArray<{ code: MiCompanyCode; pattern: string }> = [
  { code: '039', pattern: 'united guaranty mortgage indemnity' },
  { code: '044', pattern: 'national mortgage insurance' },
  { code: '013', pattern: 'united guaranty' },
  { code: '003', pattern: 'mortgage guaranty' },
  { code: '004', pattern: 'pmi mortgage' },
  { code: '001', pattern: 'enact' },
  { code: '001', pattern: 'genworth' },
  { code: '003', pattern: 'mgic' },
  { code: '011', pattern: 'republic' },
  { code: '019', pattern: 'triad' },
  { code: '033', pattern: 'radian' },
  { code: '038', pattern: 'arch' },
  { code: '043', pattern: 'essent' },
  { code: '044', pattern: 'national mi' },
  { code: '044', pattern: 'nationalmi' },
  { code: '044', pattern: 'nmi' },
];

const normalizePayeeName = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

export const resolveMiCompanyCodeFromPayeeName = (payeeName: string): MiCompanyCode | undefined => {
  const normalized = normalizePayeeName(payeeName);
  if (!normalized) return undefined;
  const tokens = normalized.split(' ');
  for (const { code, pattern } of MI_COMPANY_NAME_PATTERNS) {
    const patternTokens = pattern.split(' ');
    if (containsTokenSequence(tokens, patternTokens)) return code;
  }
  return undefined;
};

const containsTokenSequence = (tokens: readonly string[], sequence: readonly string[]): boolean => {
  if (sequence.length === 0) return false;
  for (let i = 0; i + sequence.length <= tokens.length; i += 1) {
    if (sequence.every((word, j) => tokens[i + j] === word)) return true;
  }
  return false;
};
